import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';
import { aboutPageContent } from '../content/pageContent';

const AboutContainer = styled.div`
  padding-top: 120px;
  max-width: 1200px;
  margin: 0 auto;
  padding-left: 2rem;
  padding-right: 2rem;
  padding-bottom: 3rem;
  
  @media (max-width: 768px) {
    padding-top: 90px;
    padding-left: 1rem;
    padding-right: 1rem;
  }
`;

const AboutBox = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 12px;
  border: 1px solid ${props => props.theme.colors.border};
  padding: 3rem;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 2rem 0;
  color: ${props => props.theme.colors.text};
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 600;
  margin: 2.5rem 0 1rem 0;
  color: ${props => props.theme.colors.text};
  line-height: 1.3;
  
  &:first-of-type {
    margin-top: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SubsectionTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  margin: 1.5rem 0 0.75rem 0;
  color: ${props => props.theme.colors.text};
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const Paragraph = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  margin: 0 0 1.5rem 0;
  color: ${props => props.theme.colors.textSecondary};
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const List = styled.ul`
  margin: 1rem 0 1.5rem 0;
  padding-left: 2rem;
  
  @media (max-width: 768px) {
    padding-left: 1.5rem;
  }
`;

const ListItem = styled.li`
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 0.75rem;
  color: ${props => props.theme.colors.textSecondary};
  
  strong {
    font-weight: 600;
    color: ${props => props.theme.colors.text};
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const AboutPage = () => {
  const { themeData } = useTheme();
  
  return (
    <AboutContainer>
      <AboutBox theme={themeData}>
        <PageTitle theme={themeData}>{aboutPageContent.title}</PageTitle>
        
        <Paragraph theme={themeData}>{aboutPageContent.intro}</Paragraph>
        
        {aboutPageContent.sections.map((section, index) => (
          <div key={index}>
            <SectionTitle theme={themeData}>{section.title}</SectionTitle>
            
            {section.content && (
              <Paragraph theme={themeData}>{section.content}</Paragraph>
            )}
            
            {section.list && (
              <List theme={themeData}>
                {section.list.map((item, itemIndex) => (
                  <ListItem key={itemIndex} theme={themeData}>
                    <strong>{item.title}</strong> {item.description}
                  </ListItem>
                ))}
              </List>
            )}
            
            {section.subsections && section.subsections.map((subsection, subIndex) => (
              <div key={subIndex}>
                <SubsectionTitle theme={themeData}>{subsection.title}</SubsectionTitle>
                <List theme={themeData}>
                  {subsection.points.map((point, pointIndex) => (
                    <ListItem key={pointIndex} theme={themeData}>
                      {point}
                    </ListItem>
                  ))}
                </List>
              </div>
            ))}
          </div>
        ))}
      </AboutBox>
    </AboutContainer>
  );
};

export default AboutPage;

